import {
  PROPERTY_DETAIL_TAB_IDS,
  PROPERTY_OVERVIEW_SUBTAB_IDS,
} from "@/components/PropertyDetail/tabConfig";
import { PRIVATE_AREA_TAB_INDICES } from "@/pages/properties/PrivateArea/Detail/tabConfig";

export interface CanonicalBreadcrumbItem {
  path: string;
  title: string;
  truncatable?: boolean;
}

const propertyBreadcrumbs = (
  propertyId: string,
  propertyName: string,
): CanonicalBreadcrumbItem[] => {
  const items: CanonicalBreadcrumbItem[] = [
    { path: "/properties", title: "物件一覧" },
  ];
  if (propertyId && propertyName) {
    items.push({
      path: `/properties/${propertyId}`,
      title: propertyName,
      truncatable: true,
    });
  }
  return items;
};

export const buildCanonicalPropertyBreadcrumbs = ({
  propertyId,
  propertyName,
}: {
  propertyId: string;
  propertyName: string;
}): CanonicalBreadcrumbItem[] => propertyBreadcrumbs(propertyId, propertyName);

const propertyCompositionPath = (propertyId: string) =>
  `/properties/${propertyId}?tab=${PROPERTY_DETAIL_TAB_IDS.OVERVIEW}&subtab=${PROPERTY_OVERVIEW_SUBTAB_IDS.COMPOSITION}`;

const unitDetailPath = (propertyId: string, unitId: string) =>
  `/properties/${propertyId}/units/${unitId}`;

export const buildCanonicalUnitBreadcrumbs = ({
  propertyId,
  propertyName,
  unitId,
  unitName,
}: {
  propertyId: string;
  propertyName: string;
  unitId: string;
  unitName: string;
}): CanonicalBreadcrumbItem[] => {
  const base = propertyBreadcrumbs(propertyId, propertyName);
  if (!propertyId || !propertyName) return base;
  base.push({ path: propertyCompositionPath(propertyId), title: "物件構成" });
  if (unitId && unitName) {
    base.push({
      path: unitDetailPath(propertyId, unitId),
      title: unitName,
      truncatable: true,
    });
  }
  return base;
};

export const buildCanonicalRepairBreadcrumbs = ({
  propertyId,
  propertyName,
  repairId,
  repairTitle,
}: {
  propertyId: string;
  propertyName: string;
  repairId: string;
  repairTitle: string;
}): CanonicalBreadcrumbItem[] => {
  const base = propertyBreadcrumbs(propertyId, propertyName);
  if (!propertyId || !propertyName) return base;
  base.push({
    path: `/properties/${propertyId}?tab=${PROPERTY_DETAIL_TAB_IDS.REPAIR}`,
    title: "工事",
  });
  if (repairId && repairTitle) {
    base.push({
      path: `/repairs/${repairId}`,
      title: repairTitle,
      truncatable: true,
    });
  }
  return base;
};

export const buildCanonicalEstimateBreadcrumbs = ({
  estimateId,
  estimateTitle,
}: {
  estimateId: string;
  estimateTitle: string;
}): CanonicalBreadcrumbItem[] => {
  const base: CanonicalBreadcrumbItem[] = [
    { path: "/estimates", title: "見積書一覧" },
  ];
  if (estimateId && estimateTitle) {
    base.push({
      path: `/estimates/${estimateId}`,
      title: estimateTitle,
      truncatable: true,
    });
  }
  return base;
};

export const buildCanonicalExpenseBreadcrumbs = ({
  propertyId,
  propertyName,
  expenseId,
  expenseTitle,
}: {
  propertyId: string;
  propertyName: string;
  expenseId: string;
  expenseTitle: string;
}): CanonicalBreadcrumbItem[] => {
  const base = propertyBreadcrumbs(propertyId, propertyName);
  if (!propertyId || !propertyName) return base;
  base.push(
    {
      path: `/properties/${propertyId}?tab=${PROPERTY_DETAIL_TAB_IDS.MANAGEMENT}`,
      title: "収支",
    },
    {
      path: `/properties/${propertyId}?tab=${PROPERTY_DETAIL_TAB_IDS.MANAGEMENT}&subtab=0`,
      title: "支出",
    },
  );
  if (expenseId && expenseTitle) {
    base.push({
      path: `/expenses/${expenseId}`,
      title: expenseTitle,
      truncatable: true,
    });
  }
  return base;
};

export const buildCanonicalPaymentBreadcrumbs = ({
  propertyId,
  propertyName,
  unitId,
  unitName,
  paymentId,
  paymentTitle,
}: {
  propertyId: string;
  propertyName: string;
  unitId: string;
  unitName: string;
  paymentId: string;
  paymentTitle: string;
}): CanonicalBreadcrumbItem[] => {
  const base = propertyBreadcrumbs(propertyId, propertyName);
  if (!propertyId || !propertyName) return base;
  if (unitId && unitName) {
    base.push(
      { path: propertyCompositionPath(propertyId), title: "物件構成" },
      {
        path: `${unitDetailPath(propertyId, unitId)}?tab=${PRIVATE_AREA_TAB_INDICES.TENANT}`,
        title: unitName,
        truncatable: true,
      },
      {
        path: `${unitDetailPath(propertyId, unitId)}?tab=${PRIVATE_AREA_TAB_INDICES.TENANT}&subtab=1`,
        title: "入金管理",
      },
    );
  } else {
    base.push(
      {
        path: `/properties/${propertyId}?tab=${PROPERTY_DETAIL_TAB_IDS.MANAGEMENT}`,
        title: "収支",
      },
      {
        path: `/properties/${propertyId}?tab=${PROPERTY_DETAIL_TAB_IDS.MANAGEMENT}&subtab=1`,
        title: "収入",
      },
    );
  }
  if (paymentTitle) {
    base.push({
      path: `/payments/${paymentId}`,
      title: paymentTitle,
      truncatable: true,
    });
  }
  return base;
};
